import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Tag, XCircle, Filter } from 'lucide-react';

const AdminPromoCodes = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPromoCodes();
  }, [statusFilter]);

  const fetchPromoCodes = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API_URL}/api/admin/promo-codes${params}`, {
        headers: getAuthHeaders()
      });
      setPromoCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (codeId) => {
    try {
      await axios.put(`${API_URL}/api/admin/promo-codes/${codeId}/deactivate`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Promo code deactivated');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to deactivate promo code');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700 border-green-200',
      expired: 'bg-red-100 text-red-700 border-red-200',
      exhausted: 'bg-amber-100 text-amber-700 border-amber-200',
      deactivated: 'bg-gray-100 text-gray-500 border-gray-200'
    };
    return styles[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const totalActive = promoCodes.filter(p => p.status === 'active').length;
  const totalUsage = promoCodes.reduce((sum, p) => sum + (p.usage_count || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-promo-codes">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">
              Promo Codes
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              View and manage all partner-generated promo codes
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <p className="text-sm text-[#64748B]">Total Codes</p>
              <p className="text-2xl font-bold text-[#1A1A1A]">{promoCodes.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <p className="text-sm text-[#64748B]">Active Codes</p>
              <p className="text-2xl font-bold text-green-600">{totalActive}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <p className="text-sm text-[#64748B]">Total Redemptions</p>
              <p className="text-2xl font-bold text-[#ef785b]">{totalUsage}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-[#64748B]" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] h-9 border-[#E2E2DF]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="exhausted">Exhausted</SelectItem>
              <SelectItem value="deactivated">Deactivated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ef785b]"></div>
              </div>
            ) : promoCodes.length === 0 ? (
              <div className="text-center py-12 text-[#64748B]">
                <Tag className="w-12 h-12 mx-auto mb-3 text-[#E2E2DF]" />
                <p>No promo codes found.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E2DF]">
                    <TableHead className="text-xs font-semibold text-[#64748B]">Code</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Partner</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Type / Tier</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Discount</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Usage</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Restrictions</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Expiry</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-[#64748B]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promoCodes.map((promo) => (
                    <TableRow key={promo.id} className="border-[#E2E2DF]">
                      <TableCell className="font-mono font-bold text-[#1A1A1A]">{promo.code}</TableCell>
                      <TableCell className="text-sm text-[#1A1A1A]">{promo.partner_name}</TableCell>
                      <TableCell className="text-sm text-[#64748B] capitalize">{promo.partner_type} / {promo.partner_tier}</TableCell>
                      <TableCell className="text-sm font-medium text-[#ef785b]">{promo.discount_percent}%</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{promo.usage_count}/{promo.usage_limit}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">
                        {promo.once_per_user ? (
                          <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">1x per user</span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">
                        {new Date(promo.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(promo.status)}`}>
                          {promo.status.charAt(0).toUpperCase() + promo.status.slice(1)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {promo.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivate(promo.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Deactivate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminPromoCodes;
